import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Store } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useUpdateStore } from '@/features/stores';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import {
  fromRecoverySettings,
  recoverySettingsSchema,
  toRecoverySettingsPatch,
  type RecoverySettingsForm,
} from '../schemas/recovery-settings-schema';

const mapField = (field: string) => field.replace(/^settings\.checkout\./, '');
const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'abandonmentMinutes',
  'recoveryUrlTemplate',
  'orderStatusUrlTemplate',
]);

/**
 * The recovery part of `settings.checkout`, saved through the store PATCH which merges the group.
 * Read-only without `settings.write` and on a closed store.
 */
export function RecoverySettingsCard({ store }: { store: Store }) {
  const { t } = useTranslation(['abandonedCheckouts', 'common']);
  const readOnly = !useStoreEditable({ store, permission: 'settings.write' }).editable;
  const update = useUpdateStore(store.id, 'abandonedCheckouts:toasts.settingsSaved');
  const message = useErrorMessage();
  const form = useForm<RecoverySettingsForm>({
    resolver: useI18nResolver(recoverySettingsSchema),
    defaultValues: fromRecoverySettings(store),
  });
  const domain = store.primaryDomain ?? t('abandonedCheckouts:settings.yourDomain');

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync(toRecoverySettingsPatch(values));
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: readOnly });

  return (
    <FormSection
      title={t('abandonedCheckouts:settings.title')}
      description={t('abandonedCheckouts:settings.description')}
    >
      <Form {...form}>
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <FormField
            control={form.control}
            name="abandonmentMinutes"
            render={({ field }) => (
              <FormItem className="sm:max-w-xs" data-tour="settings.recovery.minutes">
                <FormLabel>{t('abandonedCheckouts:settings.minutes')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={15}
                    max={10080}
                    disabled={readOnly}
                    {...field}
                  />
                </FormControl>
                <FormDescription>{t('abandonedCheckouts:settings.minutesHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="recoveryUrlTemplate"
            render={({ field }) => (
              <FormItem data-tour="settings.recovery.link">
                <FormLabel>{t('abandonedCheckouts:settings.recoveryUrl')}</FormLabel>
                <FormControl>
                  <Input
                    className="font-mono text-[13px]"
                    autoComplete="off"
                    placeholder={`https://${domain}/checkout/recover/{token}`}
                    disabled={readOnly}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t('abandonedCheckouts:settings.recoveryUrlHint')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="orderStatusUrlTemplate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('abandonedCheckouts:settings.statusUrl')}</FormLabel>
                <FormControl>
                  <Input
                    className="font-mono text-[13px]"
                    autoComplete="off"
                    placeholder={`https://${domain}/orders/status/{token}`}
                    disabled={readOnly}
                    {...field}
                  />
                </FormControl>
                <FormDescription>{t('abandonedCheckouts:settings.statusUrlHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {readOnly ? null : (
            <div>
              <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
                {update.isPending
                  ? t('common:actions.saving')
                  : t('abandonedCheckouts:settings.submit')}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </FormSection>
  );
}
